import { RESOURCE_TYPES } from "@/lib/resource-types";
import { SOLUTION_CATEGORIES } from "@/lib/solution-categories";
import { SITE_NAME, absoluteUrl } from "./site";

export interface BreadcrumbItem {
  name: string;
  /** Absolute URL. Empty when the crumb is text only. */
  url: string;
}

/** One editor-supplied crumb from the SEO panel. */
export interface BreadcrumbOverride {
  label?: string | null;
  url?: string | null;
}

/**
 * Labels for route segments that aren't a resource or solution index.
 * Resource and solution indexes use their own plural labels, except
 * `blog`, which reads better as "Blog" than "Blogs".
 */
const STATIC_SEGMENT_LABELS: Record<string, string> = {
  about: "About",
  features: "Features",
  packages: "Packages",
  solutions: "Solutions",
  community: "Community",
  "book-demo": "Book a Demo",
  "starter-edition": "Starter Edition",
  "legal-and-compliance": "Legal & Compliance",
  newsletters: "Newsletters",
  research: "Research",
  "disclosure-policy": "Disclosure Policy",
  resources: "Resources",
  reports: "Reports",
  blog: "Blog",
};

/**
 * Pages that exist as their own route but are not a listing index.
 * Nested entries must be listed in full so a parent like `/research`
 * stays in the trail.
 */
const STATIC_PAGE_PATHS = new Set([
  "/about",
  "/features",
  "/packages",
  "/community",
  "/book-demo",
  "/starter-edition",
  "/legal-and-compliance",
  "/newsletters",
  "/research",
  "/research/disclosure-policy",
  "/research/resources",
  "/resources",
]);

/** Indexes that have detail URLs at `/${indexUrl}/${slug}`. */
const LISTING_ROOTS = new Set([
  ...RESOURCE_TYPES.map((type) => type.indexUrl),
  ...SOLUTION_CATEGORIES.map((category) => category.indexUrl),
]);

function titleCase(slug: string): string {
  return slug
    .split("-")
    .map((part) => (part ? part[0].toUpperCase() + part.slice(1) : part))
    .join(" ");
}

function labelForResourceType(segment: string): string | undefined {
  const match = RESOURCE_TYPES.find((type) => type.indexUrl === segment);
  return match?.labels.plural;
}

function labelForSolutionCategory(segment: string): string | undefined {
  const match = SOLUTION_CATEGORIES.find(
    (category) => category.indexUrl === segment
  );
  return match?.labels.plural;
}

function labelForSegment(segment: string): string {
  return (
    STATIC_SEGMENT_LABELS[segment] ||
    labelForResourceType(segment) ||
    labelForSolutionCategory(segment) ||
    titleCase(segment)
  );
}

function isListingIndex(path: string): boolean {
  const segment = path.startsWith("/") ? path.slice(1) : path;
  return segment.length > 0 && !segment.includes("/") && LISTING_ROOTS.has(segment);
}

function isRealPage(path: string): boolean {
  return STATIC_PAGE_PATHS.has(path) || isListingIndex(path);
}

interface DeriveBreadcrumbsOptions {
  /** Path-style URL, e.g. `/blog/my-post`. Leading slash optional. */
  pagePath: string;
}

/**
 * Derive a breadcrumb trail from the public URL.
 *
 * Always starts at Binarly (`/`). A segment is included only when that
 * URL is a real page, except the current page, which is always included.
 * Parents that have no page (`/categories`, `/team`, `/forms`) are
 * skipped. Detail slugs are title-cased (`sbom-generation` → "Sbom Generation").
 */
export function deriveBreadcrumbs({
  pagePath,
}: DeriveBreadcrumbsOptions): BreadcrumbItem[] {
  const normalized = pagePath.startsWith("/") ? pagePath : `/${pagePath}`;
  const segments = normalized.split("/").filter(Boolean);

  const trail: BreadcrumbItem[] = [
    { name: SITE_NAME, url: absoluteUrl("/") },
  ];

  let cumulative = "";
  segments.forEach((segment, index) => {
    cumulative += `/${segment}`;
    const isLast = index === segments.length - 1;
    if (!isRealPage(cumulative) && !isLast) return;

    trail.push({
      name: labelForSegment(segment),
      url: absoluteUrl(cumulative),
    });
  });

  return trail;
}

function labeledOverrides(
  overrides: BreadcrumbOverride[] | null | undefined
): Array<{ label: string; url: string }> {
  if (!Array.isArray(overrides)) return [];
  return overrides
    .map((item) => ({
      label: item?.label?.trim() ?? "",
      url: item?.url?.trim() ?? "",
    }))
    .filter((item) => item.label.length > 0);
}

/** True when the SEO list should replace the automatic trail after Binarly. */
export function hasBreadcrumbOverrides(
  overrides: BreadcrumbOverride[] | null | undefined
): boolean {
  return labeledOverrides(overrides).length > 0;
}

/**
 * Automatic trail, unless `overrides` has at least one label. Those items
 * replace every crumb after Binarly. A blank link stays text only.
 */
export function resolveBreadcrumbs({
  pagePath,
  overrides,
}: DeriveBreadcrumbsOptions & {
  overrides?: BreadcrumbOverride[] | null;
}): BreadcrumbItem[] {
  const automatic = deriveBreadcrumbs({ pagePath });
  const custom = labeledOverrides(overrides);
  if (custom.length === 0) return automatic;

  return [
    automatic[0],
    ...custom.map((item) => ({
      name: item.label,
      url: item.url ? absoluteUrl(item.url) : "",
    })),
  ];
}
